import {
  isSupportedVideoLink,
  isUploadedVideoServePath,
  VIDEO_MISSING_SOURCE_MSG,
  VIDEO_UNSUPPORTED_LINK_MSG,
} from './videoSource'
import {
  countFilledAnswerOptions,
  errorDateRangeOrder,
  errorInvalidPositiveIntegerField,
  errorMinMaxSelection,
  errorOptionsNeedTwoNonEmpty,
  errorOptionalIsoDateField,
  errorPictureImageTooLargeForDraft,
  errorPictureOptionsNeedTwoImages,
  errorPictureRowsCompleteForSave,
  errorLikertScaleLabelRequired,
  errorQuestionHeadingRequired,
  errorQuestionTextRequired,
  errorRankingOptionsNeedTwoNonEmpty,
  errorShortTextLengthOrder,
} from './builderFieldMessages'
import { errorTextResponseLimits, type ChoiceTextResponseConfig } from './choiceTextResponse'
import type {
  BuilderQuestion,
  DateConfig,
  DropdownConfig,
  LikertConfig,
  RatingConfig,
  ShortTextConfig,
  SliderConfig,
  VideoConfig,
  YesNoConfig,
} from './types'
import {
  DATE_HINT_PLACEHOLDER_MAX,
  isTextAnswerConfig,
  isVideoConfig,
  LIKERT_LABEL_MAX,
  LIKERT_POINT_COUNT_MAX,
  LIKERT_POINT_COUNT_MIN,
} from './types'
import {
  effectiveSliderMaxValue,
  effectiveSliderMinValue,
  parseSliderMaxField,
  parseSliderMinField,
  parseSliderStepField,
  SLIDER_VALUE_MAX_CEILING,
  SLIDER_VALUE_MIN_FLOOR,
} from './sliderRange'

/** Validates slider min / max / step and optional end-label lengths (Figma 4399:9246). */
export function sliderRangeValidationMessage(d: SliderConfig): string | null {
  if (d.minValue.trim() !== '' && parseSliderMinField(d.minValue) === null) {
    return 'Minimum value must be a whole number'
  }
  if (d.maxValue.trim() !== '' && parseSliderMaxField(d.maxValue) === null) {
    return 'Maximum value must be a whole number'
  }
  const stepV = parseSliderStepField(d.step)
  if (stepV === null) return 'Step size must be a positive whole number'
  const minV = effectiveSliderMinValue(d.minValue)
  const maxV = effectiveSliderMaxValue(d.maxValue)
  if (minV < SLIDER_VALUE_MIN_FLOOR) return 'Minimum value cannot be less than 0'
  if (maxV > SLIDER_VALUE_MAX_CEILING) return 'Maximum value cannot be greater than 100'
  if (minV >= maxV) return 'Maximum value must be greater than minimum value'
  if (stepV > maxV - minV) return 'Step size cannot be greater than the range (maximum − minimum)'
  if ((maxV - minV) % stepV !== 0) return 'Range (maximum − minimum) must be divisible by step size'
  if (d.lowLabel.length > 100) return 'Minimum end label cannot exceed 100 characters'
  if (d.highLabel.length > 100) return 'Maximum end label cannot exceed 100 characters'
  return null
}

/** After reorder / patch, keep list titles aligned with 1-based indices. */
export function renumberBuilderQuestionTitles(rows: BuilderQuestion[]): BuilderQuestion[] {
  return rows.map((q, i) => ({ ...q, title: `Question ${i + 1}` }))
}

/** MC + checkbox share option list + min/max selection rules on save. */
function validateMcLikeSelectionsForSave(data: {
  options: readonly string[]
  minSelection: string
  maxSelection: string
  textResponse?: ChoiceTextResponseConfig | null
}): string | null {
  const optErr = errorOptionsNeedTwoNonEmpty(data.options)
  if (optErr) return optErr
  const filled = countFilledAnswerOptions(data.options)
  return (
    errorMinMaxSelection(data.minSelection, data.maxSelection, filled) ??
    errorTextResponseLimits(data.textResponse)
  )
}

function validateDropdownForSave(data: DropdownConfig): string | null {
  return errorOptionsNeedTwoNonEmpty(data.options)
}

function validateDateForSave(d: DateConfig): string | null {
  const eEar = errorOptionalIsoDateField(d.earliestSelectableDate)
  if (eEar) return eEar
  const eLate = errorOptionalIsoDateField(d.latestSelectableDate)
  if (eLate) return eLate
  const range = errorDateRangeOrder(d.earliestSelectableDate, d.latestSelectableDate)
  if (range) return range
  if (d.hintPlaceholder.length > DATE_HINT_PLACEHOLDER_MAX) {
    return `Placeholder text cannot exceed ${DATE_HINT_PLACEHOLDER_MAX} characters`
  }
  return null
}

function validateYesNoForSave(d: YesNoConfig): string | null {
  if (d.yesLabel.length > 50) return 'Option 1 label cannot exceed 50 characters'
  if (d.noLabel.length > 50) return 'Option 2 label cannot exceed 50 characters'
  return null
}

function validateRatingForSave(d: RatingConfig): string | null {
  if (d.lowLabel.length > 100) return 'Low end label cannot exceed 100 characters'
  if (d.highLabel.length > 100) return 'High end label cannot exceed 100 characters'
  return null
}

function validateLikertForSave(d: LikertConfig): string | null {
  if (d.pointCount < LIKERT_POINT_COUNT_MIN || d.pointCount > LIKERT_POINT_COUNT_MAX) {
    return 'Number of scale points must be between 1 and 10'
  }
  if (d.labels.length !== d.pointCount) return 'Likert labels must match the number of scale points'
  for (let i = 0; i < d.labels.length; i += 1) {
    const lab = d.labels[i] ?? ''
    if (lab.length > LIKERT_LABEL_MAX) {
      return `Each scale label cannot exceed ${LIKERT_LABEL_MAX} characters`
    }
    if (!lab.trim()) return errorLikertScaleLabelRequired(i + 1)
  }
  return null
}

export type BuilderSaveValidationMode = 'draft' | 'continue'

type PictureRowValidationCtx = {
  fromTemplate?: boolean
  mode?: BuilderSaveValidationMode
}

function validatePictureRowsForSave(
  options: ReadonlyArray<{ imageUrl: string; caption?: string }>,
  ctx: PictureRowValidationCtx = {},
): string | null {
  const mode = ctx.mode ?? 'draft'
  const fromTemplate = ctx.fromTemplate ?? false
  const hasAnyImage = options.some((o) => o.imageUrl.trim().length > 0)

  if (fromTemplate && !hasAnyImage && mode === 'draft') {
    const captionCount = options.filter((o) => (o.caption ?? '').trim().length > 0).length
    if (captionCount < 2) return errorPictureOptionsNeedTwoImages(options)
    return null
  }

  if (mode === 'continue') {
    const completeErr = errorPictureRowsCompleteForSave(options)
    if (completeErr) return completeErr
  } else {
    const imgErr = errorPictureOptionsNeedTwoImages(options)
    if (imgErr) return imgErr
  }
  for (const row of options) {
    if (!row.imageUrl.trim()) continue
    const big = errorPictureImageTooLargeForDraft(row.imageUrl)
    if (big) return big
  }
  return null
}

function validateRankingRowsForSave(
  options: ReadonlyArray<{ imageUrl: string; caption: string }>,
): string | null {
  const optErr = errorRankingOptionsNeedTwoNonEmpty(options)
  if (optErr) return optErr
  for (const row of options) {
    if (!row.imageUrl.trim()) continue
    const big = errorPictureImageTooLargeForDraft(row.imageUrl)
    if (big) return big
  }
  return null
}

function validateTextAnswerForSave(d: ShortTextConfig): string | null {
  const minFmt = errorInvalidPositiveIntegerField(d.minLength)
  if (minFmt) return minFmt
  const maxFmt = errorInvalidPositiveIntegerField(d.maxLength)
  if (maxFmt) return maxFmt
  const minT = d.minLength.trim()
  if (minT) {
    const n = Number(minT)
    if (n > 2000) return 'Minimum length cannot exceed 2000'
  }
  const maxT = d.maxLength.trim()
  if (maxT) {
    const n = Number(maxT)
    if (n > 2000) return 'Maximum length cannot exceed 2000'
  }
  const orderErr = errorShortTextLengthOrder(d.minLength, d.maxLength)
  if (orderErr) return orderErr
  if (d.hintPlaceholder.length > 500) return 'Placeholder text cannot exceed 500 characters'
  return null
}

function validateQuestionConfigForSave(
  q: BuilderQuestion,
  mode: BuilderSaveValidationMode = 'draft',
): string | null {
  switch (q.config.type) {
    case 'MULTIPLE_CHOICE':
    case 'CHECKBOX':
      return validateMcLikeSelectionsForSave(q.config.data)
    case 'DROPDOWN':
      return validateDropdownForSave(q.config.data)
    case 'DATE':
      return validateDateForSave(q.config.data)
    case 'YES_NO':
      return validateYesNoForSave(q.config.data)
    case 'RATING':
      return validateRatingForSave(q.config.data)
    case 'LIKERT':
      return validateLikertForSave(q.config.data)
    case 'SLIDER':
      return sliderRangeValidationMessage(q.config.data)
    case 'PICTURE_CHOICE':
      return validatePictureRowsForSave(q.config.data.options, {
        fromTemplate: q.fromTemplate,
        mode,
      })
    case 'RANKING':
      return validateRankingRowsForSave(q.config.data.options)
    default:
      break
  }
  if (isVideoConfig(q.config)) {
    return validateVideoForSave(q.config.data)
  }
  if (isTextAnswerConfig(q.config)) {
    return validateTextAnswerForSave(q.config.data)
  }
  return null
}

function validateVideoForSave(d: VideoConfig): string | null {
  const url = d.videoUrl.trim()
  if (!url) {
    return VIDEO_MISSING_SOURCE_MSG
  }
  if (d.sourceKind === 'link' && !isSupportedVideoLink(url)) {
    return VIDEO_UNSUPPORTED_LINK_MSG
  }
  if (d.sourceKind === 'upload' && !isUploadedVideoServePath(url) && !isSupportedVideoLink(url)) {
    return VIDEO_UNSUPPORTED_LINK_MSG
  }
  return validateTextAnswerForSave(d)
}

/** Per-question save validation (US-IB-03 §10.3 / BR-03). */
export function validateBuilderQuestionForSave(
  q: BuilderQuestion,
  mode: BuilderSaveValidationMode = 'draft',
): string | null {
  const textErr =
    q.config.type === 'VIDEO'
      ? errorQuestionHeadingRequired(q.questionText)
      : errorQuestionTextRequired(q.questionText)
  if (textErr) return textErr
  return validateQuestionConfigForSave(q, mode)
}

/** Question ids that fail save validation — used to scope inline field errors. */
export function builderQuestionIdsFailingSaveValidation(
  qs: readonly BuilderQuestion[],
  mode: BuilderSaveValidationMode = 'draft',
): string[] {
  const ids: string[] = []
  for (const q of qs) {
    if (validateBuilderQuestionForSave(q, mode) !== null) {
      ids.push(q.id)
    }
  }
  return ids
}

/**
 * Save / Continue validation (US-IB-03 §10.3): required question text for all types, MC/CB min options, min≤max.
 * `draft` relaxes template picture-choice rows that still have caption-only placeholders.
 * Returns a user-facing message or null when valid.
 */
export function validateBuilderQuestionsForSave(
  qs: readonly BuilderQuestion[],
  mode: BuilderSaveValidationMode = 'draft',
): string | null {
  for (const q of qs) {
    const err = validateBuilderQuestionForSave(q, mode)
    if (err) return err
  }
  return null
}
